import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Home } from "lucide-react";
import PastaLogo from "./PastaLogo";

type NavLink = {
  label: string;
  href: string;
  icon?: boolean;
  isRoute?: boolean;
  panel?: string;
  panelIndex?: number;
  contactType?: "business" | "private";
};

const navLinks: NavLink[] = [
  { label: "בית", href: "#top", icon: true },
  { label: "PRO", href: "/category/pro", isRoute: true, panel: "protein", panelIndex: 0 },
  { label: "טבעוני", href: "/category/vegan", isRoute: true, panel: "vegan", panelIndex: 1 },
  { label: "ללא גלוטן", href: "/category/gluten-free", isRoute: true, panel: "gluten-free", panelIndex: 2 },
  { label: "בלוג", href: "/recipes", isRoute: true },
  { label: "נקודות מכירה", href: "/where-to-buy", isRoute: true },
  { label: "מי אנחנו", href: "#about" },
  { label: "קטלוג עסקי", href: "/business", isRoute: true, contactType: "business" },
  { label: "לקוחות פרטיים", href: "/private", isRoute: true, contactType: "private" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let viewportH = window.innerHeight;
    let ticking = false;
    const update = () => {
      const y = window.scrollY;
      setScrolled(y > 50);
      setPastHero(y > viewportH * 0.8);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    const onResize = () => {
      viewportH = window.innerHeight;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const handleNav = (link: NavLink) => {
    if (link.isRoute) {
      navigate(link.href);
      // If link also targets a contact type and we're already on the route,
      // dispatch the type so the form pre-selects without a remount.
      if (link.contactType && location.pathname === link.href) {
        handleContact(link.contactType);
      }
      return;
    }
    if (link.contactType) {
      handleContact(link.contactType);
      return;
    }
    if (link.href === "#top") {
      if (location.pathname !== "/") {
        navigate("/");
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: link.href, panel: link.panel, panelIndex: link.panelIndex } });
      return;
    }
    // For dietary panel links, scroll to the correct position within the section
    if (link.panelIndex !== undefined) {
      const section = document.querySelector("#dietary") as HTMLElement;
      if (section) {
        const panelCount = 3;
        const sectionHeight = section.offsetHeight;
        const sectionTop = section.offsetTop;
        const scrollPerPanel = sectionHeight / panelCount;
        const targetScroll = sectionTop + (link.panelIndex * scrollPerPanel);
        window.scrollTo({ top: targetScroll, behavior: "smooth" });
        return;
      }
    }
    document.querySelector(link.href)?.scrollIntoView({ behavior: "smooth" });
  };

  const handleContact = (type: "business" | "private") => {
    const trigger = () => {
      window.dispatchEvent(new CustomEvent("set-contact-type", { detail: type }));
      setTimeout(() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }), 50);
    };
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(trigger, 300);
    } else {
      trigger();
    }
  };

  return (
    <header
      className={`fixed top-0 right-0 left-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-background/95 backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto flex items-center justify-between h-16 sm:h-20 lg:h-24 px-4 sm:px-6">
        <div className={`transition-opacity duration-300 ${location.pathname !== '/' || pastHero ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <PastaLogo size="sm" onClick={(e) => {
            if (location.pathname === '/') {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }} />
        </div>

        {/* Desktop nav - only visible on lg+ (1024px+, i.e. laptops and desktops) */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              className="font-display font-bold text-sm xl:text-base text-foreground/70 hover:text-foreground transition-colors"
              onClick={(e) => {
                e.preventDefault();
                handleNav(link);
              }}
              aria-label={link.label}
            >
              {link.icon ? <Home className="w-5 h-5" /> : link.label}
            </a>
          ))}
        </nav>

        {/* Mobile/tablet hamburger - visible below lg */}
        <button
          className="lg:hidden p-2 text-foreground"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="תפריט"
        >
          {menuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-background border-t border-border overflow-hidden"
          >
            <nav className="flex flex-col items-center gap-4 py-6">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="font-display font-bold text-lg text-foreground/70 hover:text-foreground transition-colors flex items-center gap-2"
                  onClick={(e) => {
                    e.preventDefault();
                    setMenuOpen(false);
                    handleNav(link);
                  }}
                >
                  {link.icon && <Home className="w-5 h-5" />}
                  {link.label}
                </a>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
